import I18n, {LangPackKey} from '@lib/langPack';
import type LazyLoadQueueIntersector from '@components/lazyLoadQueueIntersector';
import IS_TOUCH_SUPPORTED from '@environment/touchSupport';
import appImManager from '@lib/appImManager';
import rootScope from '@lib/rootScope';
import animationIntersector, {AnimationItemGroup} from '@components/animationIntersector';
import {horizontalMenu} from '@components/horizontalMenu';
import LazyLoadQueue from '@components/lazyLoadQueue';
import Scrollable, {ScrollableX} from '@components/scrollable';
import appSidebarRight from '@components/sidebarRight';
import StickyIntersector from '@components/stickyIntersector';
import EmojiTab, {EmojiTabCategory, getEmojiFromElement} from '@components/emoticonsDropdown/tabs/emoji';
import GifsTab from '@components/emoticonsDropdown/tabs/gifs';
import StickersTab from '@components/emoticonsDropdown/tabs/stickers';
import {MOUNT_CLASS_TO} from '@config/debug';
import {AppGifsTab} from '@components/solidJsTabs/tabs';
import {AppStickersTab} from '@components/solidJsTabs/tabs';
import findUpClassName from '@helpers/dom/findUpClassName';
import findUpTag from '@helpers/dom/findUpTag';
import blurActiveElement from '@helpers/dom/blurActiveElement';
import whichChild from '@helpers/dom/whichChild';
import cancelEvent from '@helpers/dom/cancelEvent';
import DropdownHover from '@helpers/dropdownHover';
import {IS_APPLE_MOBILE} from '@environment/userAgent';
import {AppManagers} from '@lib/managers';
import {attachClickEvent, simulateClickEvent} from '@helpers/dom/clickEvent';
import overlayCounter from '@helpers/overlayCounter';
import {getAppWindow, getOverlayRoot} from '@helpers/appWindow';
import noop from '@helpers/noop';
import {FocusDirection, ScrollOptions} from '@helpers/fastSmoothScroll';
import BezierEasing from '@vendor/bezierEasing';
import RichInputHandler from '@helpers/dom/richInputHandler';
import {getCaretPosF} from '@helpers/dom/getCaretPosNew';
import ListenerSetter from '@helpers/listenerSetter';
import {ChatRights} from '@appManagers/appChatsManager';
import {toastNew} from '@components/toast';
import ChatInput, {POSTING_NOT_ALLOWED_MAP} from '@components/chat/input';
import safeAssign from '@helpers/object/safeAssign';
import Tabs from '@components/tabs';
import StickersTabCategory from '@components/emoticonsDropdown/category';
import {Middleware} from '@helpers/middleware';
import {Accessor, createSignal, Setter} from 'solid-js';
import {getChatInputEditor} from '@components/chat/inputEditor/registry';

export const EMOTICONSSTICKERGROUP: AnimationItemGroup = 'emoticons-dropdown';

export interface EmoticonsTab {
  content: HTMLElement;
  scrollable: Scrollable;
  menuScroll?: ScrollableX;
  tabId: number;
  init: () => void;
  onOpen?: () => void;
  onOpened?: () => void;
  onClose?: () => void;
  onClosed?: () => void;
  onPeerChanged?: () => void;
}

export interface EmoticonsTabConstructable<T extends EmoticonsTab = any> {
  new(...args: any[]): T;
}

const easing = BezierEasing(0.42, 0.0, 0.58, 1.0);
const scrollOptions: Partial<ScrollOptions> = {
  forceDuration: 150,
  transitionFunction: easing,
  maxDistance: 150
};
const renderEmojiDropdownElement = (): HTMLDivElement => {
  const div = document.createElement('div');
  div.innerHTML =
    `<div class="emoji-dropdown" style="display: none;">
      <div class="emoji-container">
        <div class="tabs-container"></div>
      </div>
    </div>`;
  // the first word of `className` names the tab, the rest are extra classes on it
  const a: [string, Icon, number, LangPackKey][] = [
    ['search justify-self-start', 'search', -1, 'Search'],
    ['emoji', 'smile', 0, 'Emoji'],
    ['stickers', 'stickers_face', 1, 'AccDescr.Stickers'],
    ['gifs', 'gifs', 2, 'AccDescr.Gifs'],
    ['delete justify-self-end', 'deleteleft', -1, 'AccDescr.DeleteLastCharacter']
  ];
  const d = div.firstElementChild as HTMLDivElement;
  d.append(Tabs.Menu({
    class: 'emoji-tabs emoticons-menu no-stripe',
    children: a.map(([className, icon, tabId, ariaLabel]) => Tabs.MenuIconTab({
      icon,
      class: `emoji-tabs-${className}`,
      tab: tabId,
      label: I18n.format(ariaLabel, true)
    }))
  }) as HTMLElement);
  return d;
}

export const EMOJI_TEXT_COLOR = 'primary-text-color';

export class EmoticonsDropdown extends DropdownHover {
  public lazyLoadQueue = new LazyLoadQueue(1);

  private container: HTMLElement;
  private tabsEl: HTMLElement;
  private tabId = -1;

  private tabs: {[id: number]: EmoticonsTab};

  private searchButton: HTMLElement;
  private deleteBtn: HTMLElement;

  private selectTab: ReturnType<typeof horizontalMenu>;

  private savedRange: Range;
  private tabsToRender: EmoticonsTab[] = [];
  private managers: AppManagers;

  private rights: {[action in ChatRights]?: boolean};

  private listenerSetter: ListenerSetter;

  private _chatInput: ChatInput;
  public textColor: Accessor<string>;
  private _setTextColor: Setter<string>;

  public isStandalone: boolean;

  public animationGroup: AnimationItemGroup;

  constructor(options: {
    customParentElement?: HTMLElement | (() => HTMLElement),
    // customAnchorElement?: HTMLElement,
    getOpenPosition?: () => DOMRectEditable,
    tabsToRender?: EmoticonsTab[],
    customOnSelect?: (emoji: {element: HTMLElement} & ReturnType<typeof getEmojiFromElement>) => void,
    animationGroup?: AnimationItemGroup,
    suppressOutClick?: boolean
  } = {}) {
    super({
      element: renderEmojiDropdownElement(),
      ignoreOutClickClassName: 'input-message-input'
    });
    safeAssign(this, options);

    [this.textColor, this._setTextColor] = createSignal(EMOJI_TEXT_COLOR);

    this.listenerSetter = new ListenerSetter();
    this.isStandalone = !!options?.tabsToRender;
    this.element.classList.toggle('is-standalone', this.isStandalone);
    this.animationGroup = options.animationGroup || EMOTICONSSTICKERGROUP;

    this.rights = {
      send_gifs: this.isStandalone || undefined,
      send_stickers: this.isStandalone || undefined
    };

    this.addEventListener('open', async() => {
      if(IS_TOUCH_SUPPORTED) {
        // this.chatInput.saveScroll();
        if(blurActiveElement()) {
          // * commented to fix dynamic height of the dropdown (like limited reactions menu)
          // await pause(100);
        }
      }

      if(options.getOpenPosition) {
        const rect = options.getOpenPosition();
        this.element.style.setProperty('--top', rect.top + 'px');
        this.element.style.setProperty('--left', rect.left + 'px');
      }/*  else if(options.customAnchorElement) {
        const anchorRect = options.customAnchorElement.getBoundingClientRect();
        const offset = 64;
        this.element.style.left = anchorRect.left + 'px' as string;
        this.element.style.bottom = anchorRect.top + offset + 'px' as string;
      } */

      if(options.customParentElement) {
        const c = options.customParentElement;
        const parent = typeof(c) === 'function' ? c() : c;
        parent.append(this.element);
      } else if(this.element.parentElement !== this.chatInput.chatInput) {
        this.chatInput.chatInput.append(this.element);
      }

      this.savedRange = this.getGoodRange();

      this.lazyLoadQueue.lock();
      // this.lazyLoadQueue.unlock();
      animationIntersector.lockIntersectionGroup(this.animationGroup);

      const tab = this.tab;
      tab.onOpen?.();
    });

    this.addEventListener('opened', () => {
      animationIntersector.unlockIntersectionGroup(this.animationGroup);
      this.lazyLoadQueue.unlockAndRefresh();

      // this.container.classList.remove('disable-hover');

      const tab = this.tab;
      tab.onOpened?.();
    });

    this.addEventListener('openAfterLayout', () => {
      if(options.getOpenPosition) {
        this.element.style.setProperty('--width', this.element.offsetWidth + 'px');
      }
    });

    this.addEventListener('close', () => {
      this.lazyLoadQueue.lock();

      // нужно залочить группу и выключить стикеры
      animationIntersector.lockIntersectionGroup(this.animationGroup);
      animationIntersector.checkAnimations(true, this.animationGroup);

      const tab = this.tab;
      tab.onClose?.();
    });

    this.addEventListener('closed', () => {
      // теперь можно убрать visible, чтобы они не включились после фокуса
      animationIntersector.unlockIntersectionGroup(this.animationGroup);
      this.lazyLoadQueue.unlock();
      this.lazyLoadQueue.refresh();

      // this.container.classList.remove('disable-hover');

      this.savedRange = undefined;

      const tab = this.tab;
      tab.onClosed?.();
    });
  }

  public canUseEmoji(emoji: AppEmoji, showToast?: boolean) {
    this.init?.();
    return this.getTab(EmojiTab).canUseEmoji(emoji, undefined, showToast);
  }

  public get tab() {
    return this.tabs[this.tabId];
  }

  public get chatInput() {
    return this._chatInput || appImManager.chat.input;
  }

  public set chatInput(chatInput: ChatInput) {
    const changed = this._chatInput !== chatInput;
    this._chatInput = chatInput;
    if(changed && this.chatInput !== undefined) {
      this.checkRights();
    }
  }

  public get intersectionOptions(): IntersectionObserverInit {
    return {root: this.getElement()};
  }

  public setTextColor(textColor: string = EMOJI_TEXT_COLOR) {
    this._setTextColor(textColor);
    // this.getTab(EmojiTab)?.setTextColor(textColor);
  }

  public getTab<T extends EmoticonsTab>(instance: EmoticonsTabConstructable<T>) {
    return this.tabsToRender.find((tab) => tab instanceof instance) as T;
  }

  public init() {
    this.managers = rootScope.managers;

    if(!this.tabsToRender.length) {
      this.tabsToRender = [
        new EmojiTab({managers: this.managers, preloaderDelay: 200}),
        new StickersTab(this.managers),
        new GifsTab({managers: this.managers})
      ];
    }

    this.tabs = {};
    this.tabsToRender.forEach((tab, idx) => {
      (tab as EmojiTab).emoticonsDropdown = this;
      tab.tabId = idx;
      this.tabs[idx] = tab;
    });

    this.container = this.element.querySelector('.emoji-container .tabs-container') as HTMLDivElement;
    this.container.prepend(...this.tabsToRender.map((tab) => (tab as EmojiTab).container));
    this.tabsEl = this.element.querySelector('.emoji-tabs') as HTMLUListElement;

    this.selectTab = horizontalMenu(this.tabsEl, this.container, this.onSelectTabClick, () => {
      const {tab} = this;
      tab.init?.();
      animationIntersector.checkAnimations(false, this.animationGroup);
    });

    this.searchButton = this.element.querySelector('.emoji-tabs-search');
    this.listenerSetter.add(this.searchButton)('click', () => {
      if(this.tabId === this.getTab(StickersTab)?.tabId) {
        if(!appSidebarRight.isTabExists(AppStickersTab)) {
          appSidebarRight.createTab(AppStickersTab).open();
        }
      } else {
        if(!appSidebarRight.isTabExists(AppGifsTab)) {
          appSidebarRight.createTab(AppGifsTab).open();
        }
      }
    });

    this.deleteBtn = this.element.querySelector('.emoji-tabs-delete');
    attachClickEvent(this.deleteBtn, (e) => {
      cancelEvent(e);
      const input = this.chatInput.messageInput;
      const editor = getChatInputEditor(input);
      if(editor) {
        editor.deleteBackward();
        return;
      }

      // RichInputHandler.getInstance().makeFocused(this.chatInput.messageInput);
      let range = RichInputHandler.getInstance().getSavedRange(input);
      if(!range) {
        range = input.ownerDocument.createRange();
        range.setStartAfter(input.lastChild);
      }

      const newRange = range.cloneRange();
      // if(range.endOffset === range.startOffset && range.endContainer === range.startContainer) {
      if(range.collapsed) {
        const {node, offset} = getCaretPosF(input, range.endContainer, range.endOffset);
        let newStartNode: Node;
        if(offset) {
          newStartNode = node;
        } else {
          newStartNode = node.previousSibling;
          if(!newStartNode) {
            return;
          }

          while(newStartNode.nodeType === newStartNode.TEXT_NODE && !newStartNode.nodeValue && (newStartNode = newStartNode.previousSibling)) {

          }

          if(newStartNode.nodeType === newStartNode.ELEMENT_NODE && !(newStartNode as HTMLElement).isContentEditable) {
            return;
          }
        }

        if(newStartNode.nodeType === newStartNode.ELEMENT_NODE && (newStartNode as any).tagName === 'IMG') {
          newRange.selectNode(newStartNode);
        } else {
          const text = [...newStartNode.textContent];
          let t: string;
          if(offset) {
            let length = 0;
            t = text.find((text) => (length += text.length, length >= offset));
          } else {
            t = text.pop() || '';
          }

          const newOffset = offset ? offset - t.length : newStartNode.textContent.length - t.length;
          newRange.setStart(newStartNode, newOffset);
        }
      }

      newRange.deleteContents();

      this.chatInput.messageInputField.simulateInputEvent();
    }, {listenerSetter: this.listenerSetter});

    const HIDE_EMOJI_TAB = IS_APPLE_MOBILE && false;

    const INIT_TAB_ID = (HIDE_EMOJI_TAB ? this.getTab(StickersTab) : this.getTab(EmojiTab))?.tabId ?? this.tabsToRender[0]?.tabId ?? 0;

    if(HIDE_EMOJI_TAB) {
      (this.tabsEl.children[1] as HTMLElement).classList.add('hide');
    }

    simulateClickEvent(this.tabsEl.children[INIT_TAB_ID + 1] as HTMLElement);
    if(this.tabsToRender.length <= 1) {
      this.tabsEl.classList.add('hide');
    }
    this.tabs[INIT_TAB_ID].init?.(); // onTransitionEnd не вызовется, т.к. это первая открытая вкладка

    if(!IS_TOUCH_SUPPORTED) {
      let lastMouseMoveEvent: MouseEvent, mouseMoveTarget: HTMLElement;
      const onMouseMove = (e: MouseEvent) => {
        lastMouseMoveEvent = e;
      };
      this.listenerSetter.add(overlayCounter)('change', (isActive) => {
        if(isActive) {
          if(!mouseMoveTarget) {
            // Bind to the active app window's body (PiP-aware) and keep the exact reference so the
            // matching remove below targets the same element even if the active window changed.
            mouseMoveTarget = getOverlayRoot();
            this.listenerSetter.add(mouseMoveTarget)('mousemove', onMouseMove);
          }
        } else if(mouseMoveTarget) {
          this.listenerSetter.removeManual(mouseMoveTarget, 'mousemove', onMouseMove);
          mouseMoveTarget = undefined;
          if(lastMouseMoveEvent) {
            this.onMouseOut(lastMouseMoveEvent);
          }
        }
      });
    }

    const onPeerChanging = () => {
      if(this._chatInput || this.isStandalone) {
        return;
      }

      this.toggle(false);
    };

    const onPeerChanged = () => {
      // tabs track per-chat content (the group's own sticker set), so they are told
      // about the switch even when the dropdown itself is pinned to one chat input
      this.tabsToRender.forEach((tab) => tab.onPeerChanged?.());

      if(this._chatInput || this.isStandalone) {
        return;
      }

      this.checkRights();
    };

    this.listenerSetter.add(appImManager)('peer_changing', onPeerChanging);
    this.listenerSetter.add(appImManager)('peer_changed', onPeerChanged);
    onPeerChanged();

    const ret = super.init();
    this.init = undefined;
    return ret;
  }

  public getElement() {
    return this.element;
  }

  public scrollTo(tab: EmoticonsTab, element: HTMLElement) {
    tab.scrollable.scrollIntoViewNew({
      element: element as HTMLElement,
      axis: 'y',
      position: 'start',
      getElementPosition: tab.scrollable.container === element ? () => -element.scrollTop : undefined,
      ...scrollOptions
    });
  }

  private onSelectTabClick = (id: number) => {
    if(this.tabId === id) {
      const {tab} = this;
      this.scrollTo(tab, tab.scrollable.container as HTMLElement);
      return;
    }

    const rights: {[tabId: number]: ChatRights} = {
      ...(this.getTab(StickersTab) && {[this.getTab(StickersTab).tabId]: 'send_stickers'}),
      ...(this.getTab(GifsTab) && {[this.getTab(GifsTab).tabId]: 'send_gifs'})
    };

    const action = rights[id];
    if(action && !this.rights[action]) {
      toastNew({langPackKey: POSTING_NOT_ALLOWED_MAP[action]});
      return false;
    }

    animationIntersector.checkAnimations(true, this.animationGroup);

    this.tabId = id;
    this.searchButton.classList.toggle('hide', this.tabId === this.getTab(EmojiTab)?.tabId);
    this.deleteBtn.classList.toggle('hide', this.tabId !== this.getTab(EmojiTab)?.tabId);
  };

  private checkRights = async() => {
    this.managers ??= rootScope.managers;
    const {peerId, threadId} = this.chatInput.chat;

    const actions = Object.keys(this.rights) as ChatRights[];

    const rights = await Promise.all(actions.map((action) => {
      return this.managers.appMessagesManager.canSendToPeer(peerId, threadId, action);
    }));

    actions.forEach((action, idx) => {
      this.rights[action] = rights[idx];
    });

    if(this.init) {
      return;
    }

    const emojiTab = this.getTab(EmojiTab);
    const active = this.tabsEl.querySelector('.active');
    if(active && whichChild(active) !== (emojiTab?.tabId + 1) && (!this.rights['send_stickers'] || !this.rights['send_gifs'])) {
      this.selectTab(emojiTab.tabId, false);
    }

    emojiTab?.toggleCustomCategory();
  };

  public static menuOnClick = (
    emoticons: EmojiTab | StickersTab,
    menu: HTMLElement,
    scrollable: Scrollable,
    menuScroll?: ScrollableX,
    prevTab?: StickersTabCategory<any>,
    listenerSetter?: ListenerSetter
  ) => {
    let jumpedTo = -1;

    const scrollToTab = (tab: typeof prevTab, f?: boolean) => {
      const m = tab.menuScroll || menuScroll;
      if(m) {
        m.scrollIntoViewNew({
          element: tab.elements.menuTab,
          position: 'center',
          axis: 'x',
          getElementPosition: f ? ({elementPosition}) => {
            return elementPosition - 106;
          } : undefined,
          ...scrollOptions
        });
      }
    };

    const setActive = (tab: typeof prevTab, scroll = true) => {
      if(tab === prevTab) {
        return false;
      }

      let f = false;
      if(prevTab) {
        prevTab.elements.menuTab.classList.remove('active');
        if(prevTab.menuScroll && prevTab.menuScroll !== tab.menuScroll) {
          f = true;
          // scroll to first
          prevTab.menuScroll.container.parentElement.classList.remove('active');
          prevTab.menuScroll.scrollIntoViewNew({
            element: prevTab.menuScroll.firstElementChild as HTMLElement,
            forceDirection: scroll ? undefined : FocusDirection.Static,
            position: 'center',
            axis: 'x',
            ...scrollOptions
          });
        }
      }

      tab.elements.menuTab.classList.add('active');

      if(tab.menuScroll) {
        tab.menuScroll.container.parentElement.classList.add('active');
        scroll && menuScroll.scrollIntoViewNew({
          element: tab.menuScroll.container.parentElement,
          position: 'center',
          axis: 'x',
          ...scrollOptions
        });
      }

      if(prevTab) {
        scrollToTab(tab, f);
      }

      prevTab = tab;

      return true;
    };

    const setActiveStatic = (tab: typeof prevTab) => {
      if(prevTab?.local) {
        return;
      }

      emoticons.scrollable.scrollPosition = tab.elements.container.offsetTop + 1;
      const s = emoticons.menuScroll.container;
      const e = tab.elements.menuTab;
      s.scrollLeft = e.offsetLeft - s.clientWidth / 2 + e.offsetWidth / 2;
      setActive(tab, false);
    };

    let scrollingToContent = false;
    const stickyIntersector = new StickyIntersector(scrollable.container, (stuck, target) => {
      if(scrollingToContent) {
        return;
      }

      // console.log('sticky scrollTop', stuck, target, scrollable.container.scrollTop, jumpedTo);

      if(Math.abs(jumpedTo - scrollable.scrollPosition) <= 1) {
        return;
      } else {
        jumpedTo = -1;
      }

      const tab = emoticons.getCategoryByContainer(target);
      // the entry may describe a category deleted since the observer queued it
      if(!tab?.elements.menuTab) {
        return;
      }

      const which = whichChild(target);
      if(!stuck && (which || tab.menuScroll)) {
        return;
      }

      setActive(tab);
    });

    attachClickEvent(menu, (e) => {
      cancelEvent(e);
      let target = findUpClassName(e.target as HTMLElement, 'menu-horizontal-div-item');
      if(!target) {
        target = findUpClassName(e.target as HTMLElement, 'menu-horizontal-inner');
        if(!target || target.classList.contains('active')) {
          return;
        }

        target = target.firstElementChild.firstElementChild as HTMLElement;
      }

      const which = whichChild(target);

      const tab = emoticons.getCategoryByMenuTab(target);

      /* if(menuScroll) {
        menuScroll.scrollIntoView(target, false, 0);
      } */

      if(setActive(tab)) {
        // scrollToTab(tab);
        // return;
      }

      let offsetTop = 0, additionalOffset = 0;
      if(which > 0 || tab.menuScroll) {
        const element = tab.elements.container;
        additionalOffset = 1;
        offsetTop = element.offsetTop + additionalOffset; // * due to stickyIntersector
      }

      jumpedTo = offsetTop;

      scrollingToContent = true;
      scrollable.scrollIntoViewNew({
        element: offsetTop ? tab.elements.container : scrollable.firstElementChild as HTMLElement,
        position: 'start',
        axis: 'y',
        getElementPosition: offsetTop ? ({elementPosition}) => elementPosition + additionalOffset : undefined,
        startCallback: () => {
          if(emoticons instanceof EmojiTab && !emoticons.isCategoryVisible(tab as EmojiTabCategory)) {
            emoticons._onCategoryVisibility(tab as EmojiTabCategory, true);
          }
        },
        ...scrollOptions
      }).finally(() => {
        setActive(tab);
        scrollingToContent = false;
      });
    }, {listenerSetter});

    const a = scrollable.onAdditionalScroll ? scrollable.onAdditionalScroll.bind(scrollable) : noop;
    scrollable.onAdditionalScroll = () => {
      emoticons.content.parentElement.classList.toggle('no-border-top',
        scrollable.scrollPosition <= 0 ||
        emoticons.container.classList.contains('is-searching')
      );
      a();
    };

    emoticons.content.parentElement.classList.add('no-border-top');

    return {stickyIntersector, setActive, setActiveStatic};
  };

  public onMediaClick = async(e: {target: EventTarget | Element}, clearDraft = false, silent?: boolean, ignoreNoPremium?: boolean) => {
    const target = findUpTag(e.target as HTMLElement, 'DIV');
    if(!target) return false;

    const docId = target.dataset.docId;
    if(!docId) return false;

    return this.sendDocId({document: docId, clearDraft, silent, target, ignoreNoPremium});
  };

  public async sendDocId(options: Parameters<ChatInput['sendMessageWithDocument']>[0]) {
    if(await this.chatInput.sendMessageWithDocument(options)) {
      /* dropdown.classList.remove('active');
      toggleEl.classList.remove('active'); */
      if(emoticonsDropdown.container) {
        emoticonsDropdown.forceClose = true;
        // emoticonsDropdown.container.classList.add('disable-hover');
        emoticonsDropdown.toggle(false);
      }

      return true;
    } else {
      console.warn('got no doc by id:', document);
      return false;
    }
  }

  public addLazyLoadQueueRepeat(lazyLoadQueue: LazyLoadQueueIntersector, processInvisibleDiv: (div: HTMLElement) => void, middleware: Middleware) {
    const listenerSetter = new ListenerSetter();
    listenerSetter.add(this)('close', () => {
      lazyLoadQueue.lock();
    });

    listenerSetter.add(this)('closed', () => {
      const divs = lazyLoadQueue.intersector.getVisible();

      for(const div of divs) {
        processInvisibleDiv(div);
      }

      lazyLoadQueue.intersector.clearVisible();
    });

    listenerSetter.add(this)('opened', () => {
      lazyLoadQueue.unlockAndRefresh();
    });

    middleware.onClean(() => {
      listenerSetter.removeAll();
    });
  }

  public getSavedRange() {
    return this.getGoodRange() || this.savedRange;
  }

  private getGoodRange() {
    const sel = getAppWindow().getSelection();
    if(sel.rangeCount && getAppWindow().document.activeElement === this.chatInput?.messageInput) {
      return sel.getRangeAt(0);
    }
  }

  public destroy() {
    this.cleanup();
    this.listenerSetter.removeAll();
    this.tabsToRender.forEach((tab) => (tab as EmojiTab).destroy?.());
    this.element.remove();
  }

  public hideAndDestroy() {
    return this.toggle(false).then(() => {
      return this.destroy();
    });
  }
}

const emoticonsDropdown = new EmoticonsDropdown();
MOUNT_CLASS_TO.emoticonsDropdown = emoticonsDropdown;
export default emoticonsDropdown;
